// lib/toast.ts
import { toast, ToastOptions } from "react-toastify";

const defaultOptions: ToastOptions = {
  position: "top-right",
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

// Auth-specific toast configuration (longer duration, centered)
const authOptions: ToastOptions = {
  ...defaultOptions,
  autoClose: 5000,
  position: "top-center",
};

/**
 * Extract error message from various error types
 */
export const extractErrorMessage = (error: any): string => {
  // Axios error with response
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  // Axios error with validation errors
  if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
    return error.response.data.errors
      .map((err: any) => `${err.field}: ${err.message}`)
      .join(", ");
  }

  // Error object with message
  if (error.message) {
    return error.message;
  }

  // String error
  if (typeof error === "string") {
    return error;
  }

  // Fallback
  return "An unexpected error occurred";
};

export const showSuccess = (message: string, options?: ToastOptions) => {
  toast.success(message, {
    ...defaultOptions,
    ...options,
  });
};

export const showError = (message: string | any, options?: ToastOptions) => {
  const errorMessage = typeof message === "string" ? message : extractErrorMessage(message);
  toast.error(errorMessage, {
    ...defaultOptions,
    autoClose: 5000, // Errors stay longer
    ...options,
  });
};

export const showInfo = (message: string, options?: ToastOptions) => {
  toast.info(message, {
    ...defaultOptions,
    ...options,
  });
};

export const showWarning = (message: string, options?: ToastOptions) => {
  toast.warn(message, {
    ...defaultOptions,
    ...options,
  });
};

// Authentication-specific toasts
export const showAuthSuccess = (message: string) => {
  toast.success(message, authOptions);
};

export const showAuthError = (error: any) => {
  const message = extractErrorMessage(error);
  toast.error(message, authOptions);
};

// File validation error toast
export const showFileError = (message: string) => {
  toast.error(message, defaultOptions);
};

// Network error toast
export const showNetworkError = () => {
  toast.error("Network error. Please check your connection and try again.", {
    ...defaultOptions,
    autoClose: 6000,
  });
};

// Specific approval messages
export const showApprovalSuccess = (
  entityName: string,
  action: "approved" | "rejected",
  count?: number
) => {
  const message =
    count && count > 1
      ? `${count} ${entityName}s ${action} successfully!`
      : `${entityName} ${action} successfully!`;
  showSuccess(message);
};

export const showApprovalError = (entityName: string, error?: string) => {
  const message = error || `Failed to update ${entityName}`;
  showError(message);
};

// Batch operation feedback
export const showBatchResult = (
  entityName: string,
  total: number,
  failed: number,
  action: "approved" | "rejected"
) => {
  const successful = total - failed;

  if (failed === 0) {
    showSuccess(`All ${total} ${entityName}s ${action} successfully!`);
  } else if (successful === 0) {
    showError(`Failed to ${action.slice(0, -1)} all ${entityName}s`);
  } else {
    showWarning(`${successful} ${entityName}s ${action}, ${failed} failed`);
  }
};

// CSS classes for custom styling (optional)
export const toastClasses = {
  success: "bg-green-500 text-accent-200",
  error: "bg-red-500 text-accent-200",
  info: "bg-blue-500 text-accent-200",
  warning: "bg-yellow-500 text-black",
};
